import { FC, useEffect, useRef, useState, ReactNode } from 'react';
import * as stylex from '@stylexjs/stylex';
import { styles } from './styles';
import { Chip } from '@shared/ui/chip';
import { Tooltip } from '@shared/ui/tooltip';

interface Option<T = string> {
  label: string;
  value: T;
  disabled?: boolean;
  tooltip?: ReactNode;
}

interface SelectDropdownProps<T = string> {
  options: Option<T>[];
  value?: T;
  onChange: (value: T) => void;
  placeholder?: string;
  settings?: {
    width?: string;
    height?: string;
    padding?: string;
    fontSize?: string;
  };
  disabled?: boolean;
  selectedCount?: number;
}

export const SelectDropdown: FC<SelectDropdownProps> = ({
  options,
  value,
  onChange,
  settings = {},
  disabled = false,
  selectedCount = 0,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const renderListItem = (opt: Option) => {
    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!opt.disabled) {
        onChange(opt.value);
        setIsOpen(false);
      }
    };

    const itemStyles = stylex.props(
      styles.dropdownItem,
      opt.value === value && styles.dropdownItemSelected,
      opt.disabled && styles.dropdownItemDisabled
    );

    const listItem = (
      <li {...itemStyles}>
        <button
          type="button"
          onClick={handleClick}
          disabled={opt.disabled}
          {...stylex.props(styles.dropdownItemButton)}
        >
          {opt.label}
        </button>
      </li>
    );

    return opt.disabled && opt.tooltip ? (
      <Tooltip key={opt.value} position="top" title={opt.tooltip} isDisabledElement>
        {listItem}
      </Tooltip>
    ) : (
      <li key={opt.value} {...itemStyles}>
        <button
          type="button"
          onClick={handleClick}
          disabled={opt.disabled}
          {...stylex.props(styles.dropdownItemButton)}
        >
          {opt.label}
        </button>
      </li>
    );
  };

  return (
    <div {...stylex.props(styles.dropdownContainer)} ref={dropdownRef}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          !disabled && setIsOpen(!isOpen);
        }}
        {...stylex.props(styles.dropdownButton, disabled && styles.dropdownButtonDisabled)}
        style={settings}
        disabled={disabled}
      >
        Действия с участниками ({selectedCount > 0 ? <Chip text={selectedCount.toString()} /> : 0})
      </button>

      {isOpen && !disabled && (
        <ul {...stylex.props(styles.dropdownList)}>{options.map((opt) => renderListItem(opt))}</ul>
      )}
    </div>
  );
};
